import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AuthService, SYSTEMCATEGORY, UserData } from '../../../services/auth/auth.service';
import { Definitions } from '../../../core/definitions';
import { StorageService } from '../../../services/storage/storage.service';
import { RoutingService } from '../../../services/routing/routing.service';
import { AnalyticsService } from '../../../services/analytics/analytics.service';
import { LayoutService } from '../../../services/layout.service';
import { ErrorBibDialog } from '../dialogs/error-bib-dialog/error-bib-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { SharedService } from '../../../services/shared/shared.service';
import { DetailsService } from '../details.service';
import { DIALOG_STYLE } from '../../../../assets/css/ag/styles';
export enum RecordAction {
  ModifySearch,
  ModifyExistingRequest,
  AddToYourList,
  MultiCopy,
  PermanentLink,
  EmailThisItem,
  PrintThisItem,
  DownloadRecord,
  AddNewItemRecord,
  AddToDownloadCart,
  AddLocations,
  EditLocations,
  DeleteLocations,
  CopyCatalog,
  AuthorizeBibRecord,
  EditBibRecord,
}

interface RecordActionItem {
  label: string;
  action: RecordAction;
  type: 'search' | 'bib';
}

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ItemActionsComponent implements OnChanges {
  @Input() userData: UserData;
  @Input() fullRecord: any;
  @Input() showFormat: boolean;
  @Output() addItem = new EventEmitter<void>();
  @Output() addItemToList = new EventEmitter<void>();
  @Output() printItem = new EventEmitter<void>();
  @Output() emailItem = new EventEmitter<void>();
  @Output() multiCopy = new EventEmitter<void>();
  @Output() staffFunctionSideNav = new EventEmitter<void>();
  showModify: boolean;
  showAddToList: boolean;
  systemCatagory = SYSTEMCATEGORY;
  isKids: boolean;

  allActionItems: RecordActionItem[] = [
    {
      label: 'Modify Search',
      action: RecordAction.ModifySearch,
      type: 'search',
    },
    {
      label: 'Modify Existing Request',
      action: RecordAction.ModifyExistingRequest,
      type: 'search',
    },
    {
      label: 'Add to Your List',
      action: RecordAction.AddToYourList,
      type: 'bib',
    },
    {
      label: 'Multi Copy',
      action: RecordAction.MultiCopy,
      type: 'bib',
    },
    {
      label: 'Permanent Link',
      action: RecordAction.PermanentLink,
      type: 'bib',
    },
    {
      label: 'Email This Item',
      action: RecordAction.EmailThisItem,
      type: 'bib',
    },
    {
      label: 'Print This Item',
      action: RecordAction.PrintThisItem,
      type: 'bib',
    },
    {
      label: 'Download Record',
      action: RecordAction.DownloadRecord,
      type: 'bib',
    },
    {
      label: 'Add Item',
      action: RecordAction.AddNewItemRecord,
      type: 'bib',
    },
    {
      label: 'Add to Download Cart',
      action: RecordAction.AddToDownloadCart,
      type: 'bib',
    },
    {
      label: 'Add Locations',
      action: RecordAction.AddLocations,
      type: 'bib',
    },
    {
      label: 'Edit Locations',
      action: RecordAction.EditLocations,
      type: 'bib',
    },
    {
      label: 'Delete Locations',
      action: RecordAction.DeleteLocations,
      type: 'bib',
    },
    {
      label: 'Copy Catalog',
      action: RecordAction.CopyCatalog,
      type: 'bib',
    },
    {
      label: 'Authorize Bib Record',
      action: RecordAction.AuthorizeBibRecord,
      type: 'bib',
    },
    {
      label: 'Edit Bib Record',
      action: RecordAction.EditBibRecord,
      type: 'bib',
    },
  ];

  searchActionItems: RecordActionItem[] = [];
  actionItems: RecordActionItem[] = [];

  constructor(
    protected _storageService: StorageService,
    protected _authService: AuthService,
    protected _routingService: RoutingService,
    protected _analyticsService: AnalyticsService,
    protected _layoutService: LayoutService,
    protected _dialog: MatDialog,
    protected _sharedService: SharedService,
    protected _detailsService: DetailsService,
  ) {
    this.showModify = this._storageService.has(Definitions.searchHistorySessionKey);
    this.showAddToList = this._authService.hasDisplayYourListPermission();
    this.isKids = this._sharedService.getIsKidsCat();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this._buildFullRecordActions();
  }

  /**
   * Removes actions that are not allowed for the current user
   */
  private _buildFullRecordActions() {
    // ModifySearch
    if (!this.isKids && this.showModify) {
      this._addActionItem(RecordAction.ModifySearch);
    }
    // ModifyExistingRequest
    if (
      this.userData.isStaff &&
      this.userData.systemCategory === this.systemCatagory.SHAREIT &&
      this.fullRecord.fullRecordSettings.displayModifyRequest
    ) {
      this._addActionItem(RecordAction.ModifyExistingRequest);
    }
    // AddToYourList
    if (
      !this.isKids &&
      ['AV', 'AG', 'ZZ', 'WM'].includes(this.fullRecord.targetType) &&
      this.showAddToList
    ) {
      this._addActionItem(RecordAction.AddToYourList);
    }
    // MultiCopy
    if (
      !this.isKids &&
      this.userData.isStaff &&
      this.userData.illOptions.showMultiCopy &&
      this.showFormat
    ) {
      this._addActionItem(RecordAction.MultiCopy);
    }
    // PermanentLink
    if (!this.isKids && this.fullRecord.persistentLinkRelativeUrl) {
      this._addActionItem(RecordAction.PermanentLink);
    }
    // EmailThisItem
    if (!this.isKids && this.fullRecord.fullRecordSettings.displayEmail) {
      this._addActionItem(RecordAction.EmailThisItem);
    }
    // PrintThisItem
    if (
      (!this.isKids || this.userData.isStaff) &&
      this.fullRecord.fullRecordSettings.displayPrint
    ) {
      this._addActionItem(RecordAction.PrintThisItem);
    }
    // DownloadRecord
    if (
      !this.isKids &&
      this.userData.isStaff &&
      this.fullRecord.dowloadSettings.showDownloadLink &&
      this.fullRecord.targetType !== 'WM'
    ) {
      this._addActionItem(RecordAction.DownloadRecord);
    }
    // AddNewItemRecord
    if (
      !this.isKids &&
      this.userData.isStaff &&
      this.userData.systemCategory === this.systemCatagory.VERSO &&
      this.fullRecord.fullRecordSettings.displayVersoAddNewItem
    ) {
      this._addActionItem(RecordAction.AddNewItemRecord);
    }
    // AddToDownloadCart
    if (
      !this.isKids &&
      this.userData.isStaff &&
      this.userData.showAddToDownloadCart &&
      this.fullRecord.targetType !== 'WM'
    ) {
      this._addActionItem(RecordAction.AddToDownloadCart);
    }
    // AddLocations
    if (
      !this.isKids &&
      this.userData.isStaff &&
      this.fullRecord.fullRecordSettings.displayAddLocation &&
      this.fullRecord.targetType !== 'WM'
    ) {
      this._addActionItem(RecordAction.AddLocations);
    }
    // EditLocations
    if (
      !this.isKids &&
      this.userData.isStaff &&
      this.fullRecord.fullRecordSettings.displayEditLocation &&
      this.fullRecord.targetType !== 'WM'
    ) {
      this._addActionItem(RecordAction.EditLocations);
    }
    // DeleteLocations
    if (
      !this.isKids &&
      this.userData.isStaff &&
      this.fullRecord.fullRecordSettings.displayDeleteLocation &&
      this.fullRecord.targetType !== 'WM'
    ) {
      this._addActionItem(RecordAction.DeleteLocations);
    }
    // CopyCatalog
    if (
      !this.isKids &&
      this.userData.isStaff &&
      this.fullRecord.fullRecordSettings.displayCopyCatalog &&
      this.fullRecord.targetType !== 'WM'
    ) {
      this._addActionItem(RecordAction.CopyCatalog);
    }
    // AuthorizeBibRecord
    if (
      !this.isKids &&
      this.userData.isStaff &&
      this.fullRecord.targetType !== 'WM' &&
      this.fullRecord.authorizeOptions?.showAuthorize
    ) {
      this._addActionItem(RecordAction.AuthorizeBibRecord);
    }
    // EditBibRecord
    if (
      !this.isKids &&
      this.userData.isStaff &&
      ['AV', 'AG', 'ZZ'].includes(this.fullRecord.targetType) &&
      this.fullRecord.fullRecordSettings.displayEditBibRecord
    ) {
      this._addActionItem(RecordAction.EditBibRecord);
    }
  }

  _addActionItem(action: RecordAction) {
    this.actionItems.push(this.allActionItems.find((i) => i.action === action));
  }

  handleFullRecordAction(action: RecordAction) {
    switch (action) {
      case RecordAction.ModifySearch:
        this.modifySearch();
        break;
      case RecordAction.ModifyExistingRequest:
        this.staffFunction('staffFullRecord', 'modifyRequest');
        break;
      case RecordAction.AddToYourList:
        this.addToList();
        break;
      case RecordAction.MultiCopy:
        this.multiCopyForm();
        break;
      case RecordAction.PermanentLink:
        this.persistentLink();
        break;
      case RecordAction.EmailThisItem:
        this.email();
        break;
      case RecordAction.PrintThisItem:
        this.print();
        break;
      case RecordAction.DownloadRecord:
        this.downloadRecord();
        break;
      case RecordAction.AddNewItemRecord:
        this.openAddItem();
        break;
      case RecordAction.AddToDownloadCart:
        this.addToDownloadCart();
        break;
      case RecordAction.AddLocations:
        this.staffFunction('addLocation', 'addLocation');
        break;
      case RecordAction.EditLocations:
        this.staffFunction('editLocation', 'editLocation');
        break;
      case RecordAction.DeleteLocations:
        this.staffFunction('deleteLocation', 'deleteLocation');
        break;
      case RecordAction.CopyCatalog:
        this.staffFunction('copyCatalog', 'copyCatalog');
        break;
      case RecordAction.AuthorizeBibRecord:
        this.authorize(this.fullRecord.authorizeOptions.authorizeUrl);
        break;
      case RecordAction.EditBibRecord:
        this.editBibRecord();
        break;
    }
  }

  modifySearch() {
    this._routingService.goToAdvancedSearch(false, true);
  }

  openAddItem() {
    this.addItem.emit();
  }

  addToList() {
    this.addItemToList.emit();
  }

  email() {
    this.emailItem.emit();
  }

  print() {
    this.printItem.emit();
  }

  multiCopyForm() {
    this.multiCopy.emit();
  }

  persistentLink() {
    // ----------- generate a persistent link ----------------------------
    this._analyticsService.logEvent('Permalink Copied');
    this._layoutService.openSideNav({
      template: 'copyLink',
      position: 'end',
      data: `${this._routingService.baseUri}${this.fullRecord.persistentLinkRelativeUrl}`,
    });
  }

  downloadRecord() {
    const url = this.fullRecord.dowloadSettings.url;
    if (url) {
      window.location.href = url;
    }
  }

  staffFunction(template, type) {
    const result: any = {
      template: template,
      type: type,
      agControlId: this.fullRecord.agControlId,
      libraryDBPoolKey: this.fullRecord.libraryDBPoolKey,
      sourceKey: this.fullRecord.sourceKey,
    };

    this._layoutService.openSideNav({
      template: 'staffFullRecord',
      position: 'start',
      data: result,
    });
  }

  authorize(url) {
    this._analyticsService.logEvent('Authorize Bib Record');
    window.open(url, '_blank', 'location=yes,height=780,width=780,scrollbars=yes,status=yes');
  }

  async addToDownloadCart() {
    const response = await this._detailsService.listAddItemToCartByAgcnAsync({
      agControlId: this.fullRecord.agControlId,
      libraryDbPoolKey: this.fullRecord.libraryDBPoolKey,
    });
    if (response && response !== null) {
      if (response.status == '200 OK') {
        if (response.data && response.data.msg) {
          this._sharedService.openSnackBar(response.data.msg, 'ok');
        }
      } else {
        this._sharedService.openSnackBar('something went wrong!! Please try again', 'ok');
      }
    }
  }

  editBibRecord() {
    this._analyticsService.logEvent('Edit Bib Record');
    //TODO - stopped here tracing agcpUrl to send back in actions
    if (!this.fullRecord.agCatSettings) {
      this._routingService.goToMarcEditor(this.fullRecord.sourceKey);
    } else {
      this._launchAGCat(this.fullRecord.agCatSettings.agcpUrl, null);
    }
  }

  private _launchAGCat(agcatlink, agcatGuid) {
    const is_chrome = /chrome/i.test(navigator.userAgent);
    if (is_chrome || navigator.userAgent.indexOf('Firefox') != -1) {
      this._launchMozilla(agcatlink); // use firefox method to check
    } else if (
      navigator.appName == 'Microsoft Internet Explorer' ||
      !!(navigator.userAgent.match(/Trident/) && !navigator.userAgent.match(/MSIE/))
    ) {
      // support IE 11
      if (agcatGuid != null) {
        this._launchIE(agcatGuid);
      }
    } else {
      window.location.assign(agcatlink);
    }
  }

  // Handle FireFox & Chrome
  private _launchMozilla(agcatLink) {
    if (agcatLink) {
      window.location.assign(agcatLink);
    } else {
      let messages = '<b><h5>A newer version of AGCat is available.</h5></b><br>';
      messages +=
        'Please click the download button to get the recommended and secure version of AGCat for your computer.<br><br>';
      messages +=
        'If you elect to download later, please go to Staff Menu – Cat Admin to download the latest version of AGCat.<br><br>';
      this._openErrorBibDialog(messages);
    }
  }

  //Handle IE
  private _launchIE(agcatGuid) {
    const ieWindow = window.open('/MVC/AGCat/IECreateNewRec/' + agcatGuid);
    setTimeout(function () {
      ieWindow.close();
    }, 5000);
  }

  // Submit request pop up ILL
  private _openErrorBibDialog(messages): void {
    const dialogRef = this._dialog.open(ErrorBibDialog, {
      panelClass: DIALOG_STYLE.MD,
      disableClose: true,
      data: {
        title: messages,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result == 'yes') {
        const baseUrl = this._authService.getUserDataFromSession().agServiceBaseUrl;
        window.location.assign(`${baseUrl}/ILSP/Public/downloads/AGCatInstall.exe`);
      }
    });
  }
}
